/**
 * ToolsMenu — the eQMS record lifecycle (replaces the old CAD-PDM "Project").
 *
 * Every item is a live surface:
 *   Approvals Inbox   — opens the ReviewInbox (eis:open-approvals event)
 *   Document Control ▸ — revisions / compare / effective release (version control)
 *   Change Control ▸  — NCR / CAPA / preventive / post-market (Quality Mgmt tabs)
 *   Risk Register     — Requirements → Risk Register tab
 *   Design Review Gates — Compliance → Design Review Gates tab
 *   Traceability      — Compliance → Traceability tab
 *   Device History Record — V&V → Device History Records tab
 *   Audit Trail       — Compliance → Compliance Status → History (audit log)
 *   Declarations      — Declaration of Conformity section
 *
 * The old ECR/ECO/Merge-Request stubs are gone — their real eQMS equivalents
 * are NCR/CAPA/Change Control under Quality Management.
 */
import React, { useState } from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  AccountTree,
  Assignment,
  AssignmentInd,
  Bookmark,
  ChangeCircle,
  ChevronRight,
  Compare,
  FactCheck,
  GavelOutlined,
  GridOn,
  History,
  Loop,
  MonitorHeart,
  ReportProblemOutlined,
  Shield,
  ShieldOutlined,
  Timeline,
} from '@mui/icons-material';
import { COMPLIANCE_TABS, CONFORMANCE_MODE, COMPLIANCE_STATUS_SUBTAB } from '../../../constants/complianceTabs';

const ToolsMenu = ({
  anchorEl,
  open,
  onClose,
  hasProject,
  hasBranch,
  onSectionChange,
  onVersionControl,
  onOpenApprovals,
}) => {
  const [docCtrlAnchor, setDocCtrlAnchor] = useState(null);
  const [changeAnchor, setChangeAnchor] = useState(null);

  const closeAll = () => {
    setDocCtrlAnchor(null);
    setChangeAnchor(null);
    onClose();
  };
  const wrap = (fn) => () => { closeAll(); fn?.(); };
  const nav = (section, tab = 0, subTab = 0) => wrap(() => onSectionChange?.(section, tab, subTab));

  const row = (icon, label) => (
    <>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>{label}</ListItemText>
    </>
  );

  const gated = (node) => (
    <Tooltip title={hasProject ? '' : 'Select a product first'} placement="right">
      <span>{node}</span>
    </Tooltip>
  );

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem dense onClick={wrap(onOpenApprovals)}>
          {row(<AssignmentInd fontSize="small" />, 'Approvals Inbox…')}
        </MenuItem>
        <Divider />

        {gated(
          <MenuItem
            dense
            disabled={!hasProject}
            onClick={(e) => setDocCtrlAnchor(e.currentTarget)}
          >
            {row(<AccountTree fontSize="small" />, 'Document Control')}
            <ChevronRight fontSize="small" />
          </MenuItem>,
        )}
        {gated(
          <MenuItem
            dense
            disabled={!hasProject}
            onClick={(e) => setChangeAnchor(e.currentTarget)}
          >
            {row(<ChangeCircle fontSize="small" />, 'Change Control')}
            <ChevronRight fontSize="small" />
          </MenuItem>,
        )}
        {gated(
          <MenuItem dense disabled={!hasProject} onClick={nav('requirements', 3)}>
            {row(<Shield fontSize="small" />, 'Risk Register…')}
          </MenuItem>,
        )}
        {gated(
          <MenuItem dense disabled={!hasProject} onClick={nav('compliance', COMPLIANCE_TABS.designReview)}>
            {row(<Timeline fontSize="small" />, 'Design Review Gates…')}
          </MenuItem>,
        )}
        <Divider />

        {gated(
          <MenuItem dense disabled={!hasProject} onClick={nav('compliance', COMPLIANCE_TABS.conformance, CONFORMANCE_MODE.coverage)}>
            {row(<GridOn fontSize="small" />, 'Traceability…')}
          </MenuItem>,
        )}
        {gated(
          <MenuItem dense disabled={!hasProject} onClick={nav('vv', 1)}>
            {row(<Assignment fontSize="small" />, 'Device History Record…')}
          </MenuItem>,
        )}
        {gated(
          <MenuItem dense disabled={!hasProject} onClick={nav('compliance', COMPLIANCE_TABS.status, COMPLIANCE_STATUS_SUBTAB.history)}>
            {row(<FactCheck fontSize="small" />, 'Audit Trail…')}
          </MenuItem>,
        )}
        {gated(
          <MenuItem dense disabled={!hasProject} onClick={nav('declaration-of-conformity')}>
            {row(<GavelOutlined fontSize="small" />, 'Declarations of Conformity…')}
          </MenuItem>,
        )}
      </Menu>

      {/* Document Control ▸ — version control lives in the product view */}
      <Menu
        anchorEl={docCtrlAnchor}
        open={Boolean(docCtrlAnchor)}
        onClose={() => setDocCtrlAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem dense onClick={wrap(onVersionControl)}>
          {row(<History fontSize="small" />, 'Revisions & History…')}
        </MenuItem>
        <MenuItem dense onClick={wrap(onVersionControl)} disabled={!hasBranch}>
          {row(<Compare fontSize="small" />, 'Compare Revisions…')}
        </MenuItem>
        <MenuItem dense onClick={wrap(onVersionControl)}>
          {row(<Bookmark fontSize="small" />, 'Effective Release…')}
        </MenuItem>
      </Menu>

      {/* Change Control ▸ — Quality Management tabs */}
      <Menu
        anchorEl={changeAnchor}
        open={Boolean(changeAnchor)}
        onClose={() => setChangeAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem dense onClick={nav('quality-events', 0)}>
          {row(<ReportProblemOutlined fontSize="small" />, 'Quality Events (NCR)…')}
        </MenuItem>
        <MenuItem dense onClick={nav('quality-events', 1)}>
          {row(<Loop fontSize="small" />, 'CAPA…')}
        </MenuItem>
        <MenuItem dense onClick={nav('quality-events', 2)}>
          {row(<ShieldOutlined fontSize="small" />, 'Preventive Actions…')}
        </MenuItem>
        <MenuItem dense onClick={nav('quality-events', 3)}>
          {row(<MonitorHeart fontSize="small" />, 'Post-Market Surveillance…')}
        </MenuItem>
      </Menu>
    </>
  );
};

export default ToolsMenu;
